/*
  <fs-embed-gate> – consent-gated third-party embeds (snippets/fs-embed-gate.liquid).
  Loads the real embed (Google Maps iframe or the Elfsight Google reviews widget) only when
    1. the visitor clicks the placeholder button (remembered for this browser session per kind), or
    2. the visitor has consented to the gate's category (data-category, "preferences"): read from
       window.FSConsent.get() (assets/fs-consent.js) and updated live by the `fs:consent` event.
  Until then nothing is requested from Google or Elfsight. Auto-loading waits until the box is near the viewport.
*/
(() => {
  if (customElements.get('fs-embed-gate')) return;
  const SKEY = 'fs-eg:';
  const clicked = (kind) => {
    try { return sessionStorage.getItem(SKEY + kind) === '1'; } catch (e) { return false; }
  };
  const remember = (kind) => {
    try { sessionStorage.setItem(SKEY + kind, '1'); } catch (e) {}
  };
  const yes = (v) => v === true || v === 'yes';
  const consented = (cat) => {
    try {
      const c = window.FSConsent && window.FSConsent.get ? window.FSConsent.get() : null;
      return !!(c && yes(c[cat]));
    } catch (e) {
      return false;
    }
  };
  let elfLoading = false;
  const loadElfsight = () => {
    if (elfLoading || document.querySelector('script[src*="static.elfsight.com/platform/platform.js"]')) return;
    elfLoading = true;
    const sc = document.createElement('script');
    sc.src = 'https://static.elfsight.com/platform/platform.js';
    sc.async = true;
    document.head.appendChild(sc);
  };

  class FsEmbedGate extends HTMLElement {
    connectedCallback() {
      this.kind = this.dataset.kind || 'map';
      this.cat = this.dataset.category || 'preferences';
      this.querySelector('[data-fs-eg-load]')?.addEventListener('click', () => {
        remember(this.kind);
        this.load(true);
      });
      this.onConsent = (e) => {
        const d = (e && e.detail) || {};
        if (yes(d[this.cat])) this.autoload();
      };
      document.addEventListener('fs:consent', this.onConsent);
      const check = () => {
        if (clicked(this.kind) || consented(this.cat)) this.autoload();
      };
      check();
      // fs-consent.js may run after this file (both deferred) and loads Shopify's privacy API async: look again later.
      if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', check, { once: true });
      if (document.readyState !== 'complete') window.addEventListener('load', () => setTimeout(check, 800), { once: true });
    }
    disconnectedCallback() {
      document.removeEventListener('fs:consent', this.onConsent);
      this.io?.disconnect();
    }
    // Consent/earlier click: load lazily, once the box is within 600px of the viewport.
    autoload() {
      if (this.loaded || this.io) return;
      if (!('IntersectionObserver' in window)) return this.load(false);
      this.io = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          this.io.disconnect();
          this.load(false);
        }
      }, { rootMargin: '600px 0px' });
      this.io.observe(this);
    }
    load(focus) {
      if (this.loaded) return;
      this.loaded = true;
      document.removeEventListener('fs:consent', this.onConsent);
      this.io?.disconnect();
      const box = document.createElement('div');
      box.className = 'fs-eg__embed';
      if (this.kind === 'reviews') {
        const w = document.createElement('div');
        w.className = 'elfsight-app-' + (this.dataset.elfsight || '').replace(/[^a-z0-9-]/gi, '');
        // no data-elfsight-app-lazy: Elfsight's lazy mode waits for a scroll event, so a click would show nothing.
        // We only get here on a click or when the box is near the viewport, which is lazy enough.
        box.append(w);
        this.append(box);
        loadElfsight();
      } else {
        const f = document.createElement('iframe');
        f.src = this.dataset.src;
        f.title = this.dataset.title || 'Google Maps';
        f.loading = 'lazy';
        f.referrerPolicy = 'no-referrer-when-downgrade';
        f.allowFullscreen = true;
        box.append(f);
        this.append(box);
        if (focus) f.focus({ preventScroll: true });
      }
      this.classList.add('is-loaded');
      this.closest('.fs-show__visual')?.classList.add('is-map');
    }
  }
  customElements.define('fs-embed-gate', FsEmbedGate);
})();
